import {
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  EditButton,
  useRecordContext,
  ReferenceManyField,
  Datagrid,
  DateField,
  BooleanField,
} from 'react-admin';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Chip,
} from '@mui/material';

interface Permission {
  id: number;
  name: string;
  description?: string;
  category?: string;
}

interface Role {
  id: number;
  name: string;
  description?: string;
  user_count?: number;
  permission_count?: number;
  permissions?: Permission[];
}

const RoleShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

const RoleDetails = () => {
  const record = useRecordContext<Role>();

  if (!record) return null;

  // Group permissions by category
  const groupedPermissions =
    record.permissions?.reduce(
      (acc, permission) => {
        const category = permission.category || 'Other';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(permission);
        return acc;
      },
      {} as Record<string, Permission[]>
    ) || {};

  const categories = Object.keys(groupedPermissions).sort();

  return (
    <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Role Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="ID" value={record?.id} />
                  <InfoRow label="Name" value={record?.name} />
                  <InfoRow label="Description" value={record?.description || 'N/A'} />
                  <InfoRow label="Users" value={record?.user_count || 0} />
                  <InfoRow
                    label="Permissions"
                    value={record?.permission_count || 0}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <Box key={category} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)} (
                          {groupedPermissions[category].length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {groupedPermissions[category].map((permission) => (
                            <Chip
                              key={permission.id}
                              label={permission.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No permissions assigned
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assigned Users
                </Typography>
                <ReferenceManyField
                  reference="admin-users"
                  target="role_id"
                  perPage={25}
                  sort={{ field: 'username', order: 'ASC' }}
                >
                  <Datagrid bulkActionButtons={false} rowClick="show">
                    <TextField source="id" label="ID" />
                    <TextField source="username" />
                    <TextField source="email" />
                    <BooleanField source="is_active" label="Active" />
                    <DateField source="last_login" showTime label="Last Login" />
                  </Datagrid>
                </ReferenceManyField>
              </CardContent>
            </Card>
          </Grid>
    </Grid>
  );
};

export const RoleShow = () => {
  return (
    <Show actions={<RoleShowActions />}>
      <SimpleShowLayout>
        <RoleDetails />
      </SimpleShowLayout>
    </Show>
  );
};

const InfoRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);
